import React, { useEffect, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";

const UNITS = ["HRS", "DAY", "SERVICE", "VISIT", "MONTH", "QTY"];
const emptyForm = {
  name: "", sac_code: "", category: "", description: "",
  unit: "HRS", price: 0, tax_rate: 18, active: true,
};

export default function Services() {
  const { activeCompanyId, can, activeStoreId } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/services${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRows(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId, q]);

  const startAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const startEdit = (r) => { setEditing(r); setForm({ ...emptyForm, ...r }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        tax_rate: Number(form.tax_rate) || 0,
      };
      if (editing) {
        await api.patch(`/services/${editing.id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/services", payload);
        toast.success("Service created");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await api.delete(`/services/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const exportColumns = [
    { key: "name", label: "Service" },
    { key: "sac_code", label: "SAC Code" },
    { key: "category", label: "Category" },
    { key: "unit", label: "Unit" },
    { key: "price", label: "Price" },
    { key: "tax_rate", label: "GST %" },
    { key: "active", label: "Active", get: (r) => r.active ? "Yes" : "No" },
  ];

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><Wrench size={11} /> Catalogue</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Services</h1>
          <p className="text-sm text-muted-foreground mt-1">Labour, consulting, installation, AMC & any billable service with SAC codes.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PageExportBar
            title="Services"
            filename={`services-${new Date().toISOString().slice(0,10)}`}
            columns={exportColumns}
            rows={rows}
            testidPrefix="services"
          />
          {can("services", "create") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="h-9" onClick={startAdd} data-testid="new-service-btn">
                  <Plus size={15} weight="bold" className="mr-1.5" /> New Service
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading tracking-tight">
                    {editing ? "Edit Service" : "New Service"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="eyebrow">Service Name *</Label>
                    <Input value={form.name} required
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="service-name" autoFocus />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="eyebrow">SAC Code</Label>
                      <Input value={form.sac_code}
                        onChange={(e) => setForm({ ...form, sac_code: e.target.value })}
                        className="font-mono" placeholder="e.g. 998314" data-testid="service-sac" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="eyebrow">Category</Label>
                      <Input value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        placeholder="Installation / AMC / Consulting" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="eyebrow">Description</Label>
                    <Textarea value={form.description} rows={2}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="What's included…" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="eyebrow">Unit</Label>
                      <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                        <SelectTrigger data-testid="service-unit"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="eyebrow">Price *</Label>
                      <Input type="number" step="0.01" value={form.price} required
                        onChange={(e) => setForm({ ...form, price: e.target.value })}
                        className="font-mono" data-testid="service-price" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="eyebrow">GST %</Label>
                      <Input type="number" step="0.01" value={form.tax_rate}
                        onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                        className="font-mono" data-testid="service-gst" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Switch checked={form.active}
                      onCheckedChange={(v) => setForm({ ...form, active: v })}
                      data-testid="service-active" />
                    <Label>Active (available on invoices &amp; quotations)</Label>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="service-save-btn">
                      {editing ? "Save" : "Create"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="card-flat p-4 flex flex-wrap items-center gap-3 no-print">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search services…" className="pl-9 h-9 w-64" data-testid="services-search" />
        </div>
        <div className="text-sm text-muted-foreground ml-auto">{rows.length} service(s)</div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table" data-testid="services-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>SAC</th>
              <th>Category</th>
              <th>Unit</th>
              <th className="num">Price</th>
              <th className="num">GST %</th>
              <th>Status</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center text-muted-foreground py-6">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-muted-foreground py-10">No services yet. Click "New Service" to add one.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`service-row-${r.id}`} style={r.active ? {} : { opacity: 0.55 }}>
                <td>
                  <div className="font-medium">{r.name}</div>
                  {r.description && <div className="text-[11px] text-muted-foreground">{r.description}</div>}
                </td>
                <td className="font-mono text-[12.5px]">{r.sac_code || "—"}</td>
                <td>{r.category || "—"}</td>
                <td>{r.unit || "HRS"}</td>
                <td className="num font-mono">{inr(r.price)}</td>
                <td className="num font-mono">{r.tax_rate}%</td>
                <td>
                  <span className="text-[10.5px] uppercase tracking-wider font-semibold"
                    style={{ color: r.active ? "hsl(var(--success))" : "hsl(var(--muted-foreground))" }}>
                    {r.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="num" style={{ textAlign: "right" }}>
                  {can("services", "edit") && (
                    <Button variant="ghost" size="sm" className="h-8" onClick={() => startEdit(r)} data-testid={`service-edit-${r.id}`}>
                      <PencilSimple size={13} />
                    </Button>
                  )}
                  {can("services", "delete") && (
                    <Button variant="ghost" size="sm" className="h-8" onClick={() => del(r.id)} data-testid={`service-delete-${r.id}`}>
                      <Trash size={13} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
